/**
 * When may a screening call be paused, or resumed?
 *
 * The mirror image of shortlistGuard.js, and deliberately so: **you can pause exactly what you
 * can't shortlist.** isScreened() is the single line between them — shortlist needs a call that
 * has finished, pause needs one that hasn't. No overlap, no gap.
 *
 * Pausing tells the scheduler to stop calling. It is a hold, not a verdict: a paused candidate is
 * NOT screened, so they can't be shortlisted, and Resume is their way back.
 */

import { isScreened, isPaused } from "./screeningStatus";

const names = (rows) => rows.map((r) => r.name || r.resumeId).join(", ");

/**
 * Splits the selection three ways.
 *
 * `pausable` fails CLOSED, the same way shortlistGuard does: screeningByResumeId is filled by an
 * async poll, and a candidate missing from it reads as not-yet-scheduled — so they land in
 * notPausable, not pausable. We never post a pause for a call we can't see.
 */
export const pauseGuards = (picked, screeningByResumeId = {}) => {
  const statusOf = (r) => screeningByResumeId[r.resumeId];
  // The ranking row says a call exists; the status map says what state it's in. Both are needed —
  // and an absent status is NOT "not yet started", it's "we haven't loaded it". Acting on it would
  // mean posting a pause for a call we cannot see, so an unknown call is simply not pausable.
  const known = (r) => r.screeningScheduled && Boolean(statusOf(r));

  const pausable = picked.filter(
    (r) => known(r) && !isPaused(statusOf(r)) && !isScreened(statusOf(r))
  );
  const resumable = picked.filter((r) => known(r) && isPaused(statusOf(r)));
  const actionable = new Set([...pausable, ...resumable]);

  return {
    pausable,
    resumable,
    // No call, a finished call, or a call we can't see yet — nothing to stop, either way.
    notPausable: picked.filter((r) => !actionable.has(r)),
  };
};

/**
 * Resume when the whole selection is paused; otherwise the button offers Pause. A selection with
 * nothing in it at all is Pause by default — the guard below will block it anyway, and a button
 * that silently flips to "Resume" on an empty set would be lying about what it'd do.
 */
export const pauseMode = ({ pausable, resumable, notPausable }) =>
  resumable.length && !pausable.length && !notPausable.length ? "resume" : "pause";

/**
 * "" means allowed. Anything else is both the block and the tooltip explaining it.
 *
 * Tab first, for the same reason shortlistGuard leads with it: off the Screening tab the button is
 * disabled whoever is selected, so a per-candidate complaint would send the user to fix the wrong
 * thing.
 */
export const pauseReason = ({ stage, pausable, resumable, notPausable }) => {
  if (stage !== "screening") {
    return "Pause and resume live on the Screening tab.";
  }

  // Mixed: some paused, some still calling. One click cannot mean both — and guessing which the
  // user meant is how you resume a call someone deliberately stopped.
  if (resumable.length && (pausable.length || notPausable.length)) {
    return "Mixed selection — pause and resume can't run together. Select one or the other.";
  }

  if (notPausable.length) {
    return `Nothing to pause — ${names(notPausable)} ${
      notPausable.length === 1 ? "has" : "have"
    } no call in flight.`;
  }

  if (!pausable.length && !resumable.length) {
    return "Select a candidate whose screening call is still running.";
  }

  return "";
};
